import { LoadingButton } from '@mui/lab';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useContext, useRef, useState } from 'react';
import { ReviewCasesContext } from '../../../context/review/ReviewCasesContext';
import theme from '../../../Styles/CoreTheme';
import { ReviewDecision } from '../caseState';
import { ReviewDraft } from './useReviewDraft';

interface DecisionPanelProps {
    open: boolean;
    onClose: () => void;
    caseId: number;
    compiledNote: string;
    noteCount: number;
    onSubmitted: () => void;
    draft: ReviewDraft;
    updateDraft: (changes: Partial<ReviewDraft>) => void;
    flushDraft: () => boolean;
    ready: boolean;
    decisions: ReviewDecision[];
    expectedStatusId?: number;
}

const DecisionPanel = ({ open, onClose, caseId, compiledNote, noteCount, onSubmitted, draft, updateDraft, flushDraft, ready, decisions, expectedStatusId }: DecisionPanelProps) => {
    const { recordReviewStatus } = useContext(ReviewCasesContext);
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const selected = decisions.find(option => option.value === draft.decision);
    const state = selected?.value ?? '';
    const notes = draft.finalNote ?? compiledNote;
    const sourcesChanged = draft.finalNote !== null && draft.finalSource !== compiledNote;
    const [acknowledgedSource, setAcknowledgedSource] = useState<string | null>(null);
    const [replaceOpen, setReplaceOpen] = useState(false);
    const pending = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!state || pending.current || !ready || draft.uncertainSubmission || (sourcesChanged && acknowledgedSource !== compiledNote)) return;
        updateDraft({ finalNote: notes, finalSource: compiledNote, uncertainSubmission: true });
        if (!flushDraft()) {
            updateDraft({ uncertainSubmission: false });
            setError('Save the draft or resolve the browser storage error before submitting.');
            return;
        }
        pending.current = true;
        setSubmitting(true);
        setError(null);
        try {
            const result = await recordReviewStatus(caseId, { state, notes, expectedStatusId });
            if (!result.ok) {
                updateDraft({ uncertainSubmission: result.uncertain === true });
                flushDraft();
                setError(result.error);
                return;
            }
            onSubmitted();
        } catch {
            setError('Submission could not be confirmed. Check case history before trying again.');
        } finally {
            pending.current = false;
            setSubmitting(false);
        }
    };

    const healerVisible = selected?.healerVisible;
    const notesLabel = selected && !healerVisible ? 'Internal notes for reviewers' : 'Notes for the healer';
    const title = decisions.length === 1 && decisions[0].value === 'ESCALATION_RESPONSE' ? 'Respond to escalation' : 'Finish review';

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth='sm' fullScreen={isSmDown}>
            <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    {!ready && <Alert severity='warning'>Wait for the case sessions to load successfully before submitting.</Alert>}
                    {draft.uncertainSubmission && !submitting && (
                        <Alert severity='warning'>
                            A previous submission may have succeeded. Check the case status history before submitting again.
                            <Button color='inherit' onClick={() => updateDraft({ uncertainSubmission: false })}>
                                I checked history; this review was not saved
                            </Button>
                        </Alert>
                    )}
                    {noteCount === 0 && (
                        <Alert severity='info'>
                            You haven&apos;t written any session notes yet. You can still record a decision.
                        </Alert>
                    )}

                    <TextField
                        select
                        required
                        label='Decision'
                        value={state}
                        disabled={submitting}
                        onChange={(event) => updateDraft({ decision: event.target.value })}
                        helperText={selected?.description ?? 'Choose what happens to this case next.'}
                        fullWidth
                    >
                        {decisions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Stack direction='row' justifyContent='space-between' spacing={2} sx={{ width: '100%' }}>
                                    <span>{option.label}</span>
                                    <Typography component='span' variant='caption' color={option.healerVisible ? 'success.dark' : 'text.secondary'}>
                                        {option.healerVisible ? 'Visible to healer' : 'Internal'}
                                    </Typography>
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>

                    {selected && (
                        <Alert severity={healerVisible ? 'success' : 'info'} icon={healerVisible ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}>
                            {healerVisible
                                ? 'The healer will see this decision and the notes below.'
                                : 'Internal only. The healer continues to see “Submitted” and will not see these notes.'}
                        </Alert>
                    )}

                    <Box>
                        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                                {notesLabel}
                            </Typography>
                            {draft.finalNote !== null && (
                                <Button
                                    size='small'
                                    disabled={submitting}
                                    onClick={() => setReplaceOpen(true)}
                                >
                                    Rebuild from session notes
                                </Button>
                            )}
                        </Stack>
                        <TextField
                            label={notesLabel}
                            disabled={submitting}
                            value={notes}
                            onChange={(event) => {
                                updateDraft({ finalNote: event.target.value, finalSource: draft.finalNote === null ? compiledNote : draft.finalSource });
                            }}
                            placeholder='Your session notes appear here. Edit freely before submitting.'
                            multiline
                            minRows={10}
                            maxRows={20}
                            fullWidth
                        />
                        <Typography variant='caption' color='text.secondary'>
                            {selected && !healerVisible ? 'Only reviewers can read this text.' : 'This exact text is what the healer will read.'}
                        </Typography>
                    </Box>

                    {sourcesChanged && (
                        <Box>
                            <Alert severity='warning'>Session or overall notes changed after this message was edited. Compare the current notes before submitting.</Alert>
                            <TextField label='Current source notes' value={compiledNote} multiline minRows={3} maxRows={8} fullWidth InputProps={{ readOnly: true }} sx={{ mt: 2 }} />
                            <FormControlLabel
                                control={<Checkbox disabled={submitting} checked={acknowledgedSource === compiledNote} onChange={(_, checked) => setAcknowledgedSource(checked ? compiledNote : null)} />}
                                label='I reconciled the changes; submit the final message above'
                            />
                        </Box>
                    )}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={submitting} color='inherit'>
                    Back
                </Button>
                <LoadingButton onClick={handleSubmit} loading={submitting} disabled={!state || !ready || draft.uncertainSubmission || (sourcesChanged && acknowledgedSource !== compiledNote)} variant='contained'>
                    Submit
                </LoadingButton>
            </DialogActions>
            <Dialog open={replaceOpen} onClose={() => setReplaceOpen(false)} aria-labelledby='replace-review-title'>
                <DialogTitle id='replace-review-title'>Replace the final message?</DialogTitle>
                <DialogContent>Your manual edits will be replaced with the current session and overall notes.</DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplaceOpen(false)}>Cancel</Button>
                    <Button onClick={() => { updateDraft({ finalNote: null, finalSource: '' }); setReplaceOpen(false); }}>Replace message</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default DecisionPanel;
